import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Button, Card, CardContent } from '@/components/ui';
import { OrgSidebar } from '@/components/public/org/OrgSidebar';
import { useOrgMemberStore } from '@/store/orgMember';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import type { MemberCourtHireBookingRow } from '@/services/memberPortal';
import type { MemberPortalResponse } from '@/services/publicClubMembership';
import { isCourtHireBookingCalendarVisible, type CourtHireBookingStatus } from '@court-sync/shared';
import {
  MembershipHouseholdSection,
  MembershipMemberSinceCard,
} from '@/components/membership/membershipPortalShared';

interface OrgConsumerSidebarProps {
  hasMemberships: boolean;
  hasCourtHire: boolean;
  courtBookings: MemberCourtHireBookingRow[];
  membershipData: MemberPortalResponse | null | undefined;
  membershipLoading: boolean;
}

export function OrgConsumerSidebar({
  hasMemberships,
  hasCourtHire,
  courtBookings,
  membershipData,
}: OrgConsumerSidebarProps) {
  const orgProfile = useOrgMemberStore((s) => s.orgProfile);
  const { p } = useOrgPaths();

  const courtBookingsPath = p('/court-hire/bookings');
  const membershipPath = p('/membership');

  const memberships = membershipData?.memberships ?? [];

  const upcomingCourts = courtBookings
    .filter((b) => isCourtHireBookingCalendarVisible(b.status as CourtHireBookingStatus))
    .filter((b) => new Date(b.startAt) >= new Date())
    .slice(0, 3);

  return (
    <aside className="space-y-6" aria-label="Member information">
      {hasMemberships ? (
        <>
          <MembershipMemberSinceCard memberships={memberships} viewHref={membershipPath} />
          <MembershipHouseholdSection memberships={memberships} />
        </>
      ) : null}

      {hasCourtHire && upcomingCourts.length > 0 ? (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold">Upcoming courts</h3>
              {courtBookingsPath ? (
                <Button asChild variant="ghost" size="sm" className="h-auto px-2 py-1 text-xs">
                  <Link to={courtBookingsPath}>View all</Link>
                </Button>
              ) : null}
            </div>
            <ul className="space-y-3">
              {upcomingCourts.map((booking) => (
                <li key={booking.id} className="text-sm">
                  <p className="font-medium">{booking.courtName ?? 'Court'}</p>
                  <p className="text-muted-foreground">
                    {format(new Date(booking.startAt), 'EEE d MMM, h:mm a')}
                  </p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {orgProfile ? (
        <OrgSidebar
          organization={orgProfile.organization}
          amenities={orgProfile.amenities}
          sports={orgProfile.sports}
          locations={orgProfile.locations}
          operatingHours={orgProfile.operatingHours}
        />
      ) : null}

    </aside>
  );
}
